import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as api from '@/features/backlog/api';
import { Comment, MentionComment } from '@/features/backlog/types';
import { renderCommentBody } from '@/features/backlog/mentions';
import { timeAgo } from '@/features/backlog/format';
import { taskPath, wikiLegacyPagePath } from '@/constants/routes';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { Check } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import MentionInput from '@/components/mention-input';
import { cn } from '@/components/ui/cn';

/**
 * The "@mentions" panel — every comment that mentions you, newest first, with a link to the task, the
 * comment rendered with highlighted mentions, a resolve toggle, and an inline reply. Rendered as the
 * Mentions TAB inside the notification bell's flyout (see notification-bell). `onCount` reports the
 * unresolved count for the tab badge; `close` closes the enclosing flyout on navigation.
 */
export function MentionsPanel({ close, onCount }: { close: () => void; onCount?: (n: number) => void }) {
  const { me } = useIdentity();
  const { members, boards } = useAppData();
  const navigate = useNavigate();
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const activeMembers = useMemo(() => [...members].filter((m) => m.active).sort((a, b) => a.name.localeCompare(b.name)), [members]);
  const [list, setList] = useState<MentionComment[]>([]);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  // Replies I post from the inbox this session, keyed by the mention they answer — so they show
  // inline immediately (a reply I make won't necessarily mention me, so it wouldn't return on reload).
  const [replies, setReplies] = useState<Record<string, Comment[]>>({});
  const [justReplied, setJustReplied] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .listMyMentions(40)
      .then((ms) => {
        setList(ms);
        onCount?.(ms.filter((m) => !m.resolved).length);
      })
      .catch(() => undefined);
  }, [onCount]);

  useEffect(() => {
    if (!me) return undefined;
    load();
    const t = window.setInterval(load, 60000);
    return () => window.clearInterval(t);
  }, [me, load]);

  if (!me) return null;

  const goTo = (c: MentionComment) => {
    if (c.source === 'wiki' && c.page_id) {
      navigate(c.comment_thread_id ? `${wikiLegacyPagePath(c.page_id)}?thread=${c.comment_thread_id}` : wikiLegacyPagePath(c.page_id));
      close();
      return;
    }
    const b = boardById.get(c.board_id);
    if (b && c.item_key) navigate(taskPath(b.slug, c.item_key));
    close();
  };
  const toggleResolve = (c: MentionComment) => {
    const next = !c.resolved;
    const nl = list.map((x) => (x.id === c.id ? { ...x, resolved: next } : x));
    setList(nl);
    onCount?.(nl.filter((x) => !x.resolved).length);
    api.setCommentResolved(c.id, next).catch(() => load());
  };
  // Wiki mentions are live-scanned (no comment to resolve) — toggling grays them out (like item
  // mentions) and persists a per-member read; a dismissed page mention resurfaces on a later edit.
  const toggleWikiRead = (c: MentionComment) => {
    const next = !c.resolved;
    const nl = list.map((x) => (x.id === c.id ? { ...x, resolved: next } : x));
    setList(nl);
    onCount?.(nl.filter((x) => !x.resolved).length);
    api.dismissWikiMention(c.id, next).catch(() => load());
  };
  const sendReply = async (c: MentionComment) => {
    if (!draft.trim()) return;
    const body = draft;
    setDraft('');
    setReplyTo(null);
    try {
      const created = await api.createComment(c.item_id, { body, parent_id: c.id });
      setReplies((r) => ({ ...r, [c.id]: [...(r[c.id] ?? []), created] }));
      setJustReplied(c.id);
      window.setTimeout(() => setJustReplied((v) => (v === c.id ? null : v)), 2500);
    } catch {
      /* best-effort */
    }
  };

  return (
    <div>
      {list.length === 0 ? (
        <div className="px-3 py-10 text-center text-[12px] text-fg-faint">No one has mentioned you yet.</div>
      ) : (
        list.map((c) => {
          const author = memberById.get(c.author_id ?? '');
          return (
                  <div key={c.id} className={cn('rounded-lg px-2 py-2 transition-colors', c.resolved ? 'opacity-60' : '')}>
                    <div className="flex items-center gap-2 text-[11px] text-fg-faint">
                      <MemberAvatar member={author} size={20} className="shrink-0" />
                      <span className="font-medium text-fg-soft">{author?.name ?? 'Someone'}</span>
                      {c.source === 'wiki' ? (
                        <button
                          type="button"
                          onClick={() => goTo(c)}
                          className="inline-flex max-w-[180px] items-center gap-1 rounded-md border border-line bg-surface-raised px-1.5 py-px text-[11px] font-semibold text-fg-soft transition-colors hover:border-line-strong hover:bg-layer"
                          title={c.page_title}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-accent"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
                          <span className="truncate">{c.page_title || 'Wiki page'}</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => goTo(c)}
                          className="item-key rounded px-1 py-px tnum text-[11px] font-semibold transition-opacity hover:opacity-80"
                          title={c.item_title}
                        >
                          {c.item_key}
                        </button>
                      )}
                      <span>{timeAgo(c.created_at)}</span>
                      {/* Item mentions resolve the comment; wiki mentions (live-scanned) toggle a per-member
                          read. Both use the same check → gray-out affordance for consistency. */}
                      <button
                        type="button"
                        onClick={() => (c.source === 'wiki' ? toggleWikiRead(c) : toggleResolve(c))}
                        title={c.resolved ? 'Mark unread' : 'Mark read'}
                        className={cn('ml-auto flex h-6 w-6 items-center justify-center rounded text-fg-faint hover:bg-layer-strong', c.resolved ? 'text-emerald-500' : 'hover:text-emerald-500')}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mt-0.5 whitespace-pre-wrap pl-7 text-[12.5px] text-fg-soft">{renderCommentBody(c.body, memberById, me.id)}</div>
                    {(replies[c.id] ?? []).length > 0 ? (
                      <div className="ml-7 mt-1.5 space-y-1.5 border-l border-line pl-2.5">
                        {(replies[c.id] ?? []).map((r) => {
                          const ra = memberById.get(r.author_id ?? '');
                          return (
                            <div key={r.id} className="flex items-start gap-2">
                              <MemberAvatar member={ra} size={18} className="mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <div className="text-[11px] font-medium text-fg-soft">{ra?.name ?? 'You'}</div>
                                <div className="whitespace-pre-wrap text-[12.5px] text-fg-soft">{renderCommentBody(r.body, memberById, me.id)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    <div className="mt-1 pl-7">
                      {replyTo === c.id ? (
                        <div>
                          <MentionInput value={draft} onChange={setDraft} members={activeMembers} onSubmit={() => sendReply(c)} placeholder="Reply… (@ to mention)" />
                          <div className="mt-1 flex justify-end gap-2">
                            <button type="button" onClick={() => { setReplyTo(null); setDraft(''); }} className="text-[12px] text-fg-faint hover:text-fg">Cancel</button>
                            <button type="button" onClick={() => sendReply(c)} disabled={!draft.trim()} className="text-[12px] font-semibold text-accent disabled:opacity-40">Reply</button>
                          </div>
                        </div>
                      ) : c.source === 'wiki' ? null : (
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => { setReplyTo(c.id); setDraft(''); }} className="text-[11.5px] font-medium text-fg-faint transition-colors hover:text-accent">
                            Reply
                          </button>
                          {justReplied === c.id ? <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-500"><Check className="h-3 w-3" /> Replied</span> : null}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
    </div>
  );
}
